import React, { useState } from 'react'

import type { UploadProps } from '@fourthwall/components'
import { ButtonClean, Subsection, Toasts, Upload } from '@fourthwall/components'
import { useFormikContext } from '@fourthwall/utils/dist/formik'
import { getRejectionErrors } from '@utils/dropzone'

import { LiveStreamKeys } from '../../components/LiveStreamKeys/LiveStreamKeys'
import { MuxPlayer } from '../../components/MuxPlayer'
import { FormFields, type FormValues } from '../../types'
import * as S from './styled'

type Props = {
  streamHasEnded: boolean
  playbackId?: string
  playbackToken?: string
  rtmpsUrl?: string
  srtUrl?: string
  streamKey?: string
}

export const LiveStreamRecordingFields = ({
  streamHasEnded,
  playbackId,
  playbackToken,
  rtmpsUrl,
  srtUrl,
  streamKey,
}: Props) => {
  const [showStreamURLAndKey, setShowStreamURLAndKey] = useState(false)

  const { values, setFieldValue } = useFormikContext<FormValues>()

  const handleFileReject: UploadProps['onFileDropRejected'] = (rejectedFiles) => {
    getRejectionErrors(rejectedFiles).forEach((rejection) => {
      Toasts.notify(rejection)
    })
  }

  const handleVideoThumbnailUpload = (file?: File) => {
    if (file) {
      setFieldValue(FormFields.videoThumbnail, file)
    }
  }

  const renderThumbnailButton = () => {
    if (!values.videoThumbnail) {
      return (
        <div>
          <Upload
            accept={{
              'image/*': [],
            }}
            maxSize={10485760}
            // eslint-disable-next-line react/no-unstable-nested-components
            content={() => <ButtonClean label="Upload custom thumbnail" />}
            onFileDropRejected={handleFileReject}
            onFileDrop={(files) => handleVideoThumbnailUpload(files?.[0])}
          />
        </div>
      )
    }

    return (
      <div>
        <ButtonClean
          label="Remove custom thumbnail"
          onClick={() => {
            setFieldValue(FormFields.videoThumbnail, undefined)
          }}
        />
      </div>
    )
  }

  if (streamHasEnded) {
    if (playbackId && playbackToken) {
      const poster =
        typeof values.videoThumbnail === 'object'
          ? URL.createObjectURL(values.videoThumbnail as File)
          : values.videoThumbnail

      return (
        <Subsection title="Livestream recording">
          <S.Container>
            <MuxPlayer
              playbackId={playbackId}
              tokens={{ playback: playbackToken }}
              streamType="on-demand"
              poster={poster}
            />
            {renderThumbnailButton()}
          </S.Container>
        </Subsection>
      )
    }

    return (
      <Subsection title="Livestream recording">
        <div>Your recording is being processed. This might take a few minutes.</div>
      </Subsection>
    )
  }

  return (
    <Subsection title="Livestream recording">
      <S.Container>
        {(!playbackId || !playbackToken) && 'Your recording will be saved here after stream ends.'}

        <S.ButtonsContainer>
          {!showStreamURLAndKey && (
            <ButtonClean
              label="Show stream URL and key"
              withUnderline
              onClick={() => setShowStreamURLAndKey(true)}
            />
          )}

          {renderThumbnailButton()}
        </S.ButtonsContainer>

        {showStreamURLAndKey && (
          <LiveStreamKeys
            rtmpsUrl={rtmpsUrl || ''}
            srtUrl={srtUrl || ''}
            streamKey={streamKey || ''}
          />
        )}
      </S.Container>
    </Subsection>
  )
}
